import { SectionCard, STATUS_LABELS, type StatusRole } from '@lighthouse/design'
import { PipelineFlow, type FlowEdge, type FlowNode, type FlowTone } from './PipelineFlow'
import { NOT_MEASURED, agoLabel, exactNumber } from '../../utils/format'
import type { EtlRunDetail } from '../../types/etlRuns'

/**
 * The Pipeline View: one entity run as a funnel (doc 01 §3.2).
 *
 * Extracted → Transformed (rejected rows leave as a side edge) → Keys (resolved
 * and unresolved lookups) → Written (inserted, updated, deleted) → Validation.
 * The counters are the run's own (ObservatoryEtlRunsController, `records`), so
 * this adds no telemetry. It only draws what the Record Summary lists.
 *
 * What it will not do:
 * - Draw Validation as passed. `health.validation` is null until data-quality
 *   results are correlated to a run id, and nothing does that yet. So the node
 *   renders as unobserved: a dash, dashed and neutral, and saying so.
 * - Hide a zero. Every counter is measured, so 0 prints as 0. Only the success
 *   rate can be absent, and it then renders NOT_MEASURED with the server's reason.
 * - Present a running or stale run's counts as final. A running run's counters
 *   grow at every chunk boundary. A stale run's stopped at its last heartbeat.
 * - Compute a ratio. The one rate on the flow is the server's, which already
 *   declines to divide when the two counts do not describe the same rows.
 */

/** Why the Validation node is a dash. */
const VALIDATION_UNCORRELATED = 'Data quality is not correlated to runs'

const ROLE_TONE: Record<StatusRole, FlowTone> = {
  good: 'neutral',
  warning: 'warning',
  serious: 'warning',
  critical: 'critical',
}

/** "98.76%": the server rounds to two places, so no more rounding happens here. */
function percent(rate: number): string {
  return `${rate.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
}

/**
 * What the counts are, when they are not final.
 *
 * A skipped entity is its own outcome on the runs endpoint, as on Execution
 * History: both read ExecutionOutcomes.ForEntity. `outcome` is still read as a
 * string, so a value this build does not know falls through to no note.
 */
function progressNote(run: EtlRunDetail): string | null {
  const outcome: string = run.outcome
  switch (outcome) {
    case 'running':
      return 'Counts so far, in progress. They grow at every chunk boundary.'
    case 'stale':
      return `Counts as of the last heartbeat (${agoLabel(run.lastHeartbeatAt)}). The worker stopped reporting, `
        + 'so this run is not in progress and these counts are not final.'
    case 'pending':
      return 'Not started. No rows have moved yet.'
    case 'failed':
      return 'Counts as the run left them when it failed. The rows they describe are committed, '
        + 'and a resume does not undo them.'
    case 'skipped':
      return 'Skipped: the source object does not exist, so nothing was read. The zeros are measured.'
    default:
      return null
  }
}

/** The node the live chunk is at, and what it is doing there. Only a running run has a live phase. */
function livePosition(run: EtlRunDetail): { nodeId: string; status: string } | null {
  if (run.livePhase === null) return null
  // liveChunkIndex is zero-based (types/liveOperations.ts); people count chunks from one.
  const chunk = run.liveChunkIndex === null ? 'now' : `chunk ${run.liveChunkIndex + 1}`
  return run.livePhase === 'landing'
    ? { nodeId: 'extracted', status: `Landing ${chunk}` }
    : { nodeId: 'transformed', status: `Transforming ${chunk}` }
}

function buildFlow(run: EtlRunDetail): { nodes: FlowNode[]; edges: FlowEdge[] } {
  const { records, health } = run
  const live = livePosition(run)
  const rate = health.successRatePercent
  const validation = health.validation

  const nodes: FlowNode[] = [
    {
      id: 'extracted',
      label: 'Extracted',
      figures: [{ value: exactNumber(records.extracted), label: 'rows read' }],
      caption: 'landed from the source',
    },
    {
      id: 'transformed',
      label: 'Transformed',
      figures: [{ value: exactNumber(records.transformed), label: 'rows' }],
      caption: 'through cleaning and de-duplication',
    },
    {
      id: 'keys',
      label: 'Keys',
      figures: [
        { value: exactNumber(records.keysResolved), label: 'resolved' },
        {
          value: exactNumber(records.keysUnresolved),
          label: 'unresolved',
          tone: records.keysUnresolved > 0 ? 'warning' : 'neutral',
        },
      ],
      caption: 'foreign-key lookups',
    },
    {
      id: 'written',
      label: 'Written',
      figures: [
        { value: exactNumber(records.inserted), label: 'inserted' },
        { value: exactNumber(records.updated), label: 'updated' },
        { value: exactNumber(records.deleted), label: 'deleted' },
      ],
    },
    validation === null
      ? {
          id: 'validation',
          label: 'Validation',
          figures: [{ value: NOT_MEASURED, label: 'for this run' }],
          status: 'Not measured',
          caption: VALIDATION_UNCORRELATED,
          tone: 'unobserved',
        }
      : {
          id: 'validation',
          label: 'Validation',
          figures: [{ value: STATUS_LABELS[validation], label: 'as served', tone: ROLE_TONE[validation] }],
          tone: ROLE_TONE[validation],
        },
  ]

  if (live) {
    const at = nodes.findIndex((n) => n.id === live.nodeId)
    if (at >= 0) nodes[at] = { ...nodes[at], tone: 'active', status: live.status }
  }

  const edges: FlowEdge[] = [
    {
      from: 'extracted',
      to: 'transformed',
      metric: {
        value: rate === null ? NOT_MEASURED : percent(rate),
        label: run.outcome === 'running' ? 'transformed so far' : 'transformed',
      },
    },
    {
      from: 'transformed',
      metric: {
        value: exactNumber(records.rejected),
        label: 'rejected / filtered',
        tone: records.rejected > 0 ? 'warning' : 'neutral',
      },
    },
    // No metric on the remaining edges: nothing measures what passes along them
    // apart from the counts the nodes already show.
    { from: 'transformed', to: 'keys' },
    { from: 'keys', to: 'written' },
    { from: 'written', to: 'validation' },
  ]

  return { nodes, edges }
}

export function RunPipelinePanel({ run }: { run: EtlRunDetail }) {
  const { nodes, edges } = buildFlow(run)
  const progress = progressNote(run)

  const notes: string[] = []
  if (run.records.caveat) notes.push(run.records.caveat)
  if (run.health.successRatePercent === null && run.health.successRateUnavailableReason) {
    notes.push(`No transform rate: ${run.health.successRateUnavailableReason}`)
  }
  if (run.records.keysUnresolved > 0) {
    notes.push(
      'An unresolved key is counted, never rejected: the row is still written, and a later run fills '
        + 'the key once its parent row lands.',
    )
  }

  return (
    <SectionCard
      title="Record flow"
      subtitle={progress ?? 'Final counts, as the run recorded them.'}
    >
      <div className="lh-pflow-panel" data-outcome={run.outcome}>
        <PipelineFlow nodes={nodes} edges={edges} label={`Pipeline for ${run.entityName}`} />
        {notes.length > 0 && (
          <ul
            className="lh-pflow-notes"
            style={{
              margin: '10px 0 0',
              paddingLeft: 18,
              fontSize: 12,
              lineHeight: 1.4,
              color: 'var(--color-text-secondary)',
            }}
          >
            {notes.map((note) => <li key={note}>{note}</li>)}
          </ul>
        )}
      </div>
    </SectionCard>
  )
}
